import { useState } from "react";
import { ExistingProject } from "../../models/project";
import { ExistingCraftRecord } from "../../models/craft-record";
import { ExistingCustomer } from "../../models/customer";
import { ExistingTask } from "../../models/task";
import { ExistingStiltInvoice } from "../../models/invoice";
import { ExistingEstimate } from "../../models/estimate";
import { ExistingAttachment } from "../../models/attachment";
import { TaskStatus } from "../../models/task-status";
import ProjectHeader from "./ProjectHeader";
import CraftRecordList from "./CraftRecordList";
import ProjectStats from "./ProjectStats";
import ProjectPhotoGallery from "./ProjectPhotoGallery";
import { PhotoForGallery } from "./ProjectPhotoGallery";
import BaseButtonSecondary from "../../components/BaseButtonSecondary";

interface Props {
  project: ExistingProject;
  craftRecords: ExistingCraftRecord[];
  customer: ExistingCustomer | null;
  tasksByCraftRecord: Record<string, ExistingTask[]>;
  invoicesByCraftRecord: Record<string, ExistingStiltInvoice[]>;
  estimatesByCraftRecord: Record<string, ExistingEstimate[]>;
  photosByCraftRecord: Record<string, PhotoForGallery[]>;
  attachments: ExistingAttachment[];
  userDisplayNamesMap: Record<string, string>;
  isUserDisplayNamesLoading: boolean;
  siteKey: string;
  onGeneratePDF: () => void;
  generatingPDF: boolean;
  onEditProject: () => void;
}

export default function ProjectDetailsPage({
  project,
  craftRecords,
  customer,
  tasksByCraftRecord,
  invoicesByCraftRecord,
  estimatesByCraftRecord,
  photosByCraftRecord,
  attachments,
  userDisplayNamesMap,
  isUserDisplayNamesLoading,
  siteKey,
  onGeneratePDF,
  generatingPDF,
  onEditProject,
}: Props) {
  const [expandedJobs, setExpandedJobs] = useState<Record<string, boolean>>({});
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    jobs: true,
    projectSummary: true,
    projectActivity: false,
  });
  const [isPhotoGalleryOpen, setIsPhotoGalleryOpen] = useState(false);

  // Toggle expanded state for a job
  const toggleJobExpand = (jobId: string) => {
    setExpandedJobs((prev) => ({
      ...prev,
      [jobId]: !prev[jobId],
    }));
  };

  // Toggle expanded state for a section
  const toggleSectionExpand = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Calculate project stats
  const totalInvoiceAmount = Object.values(invoicesByCraftRecord)
    .flat()
    .filter((invoice) => invoice.status !== "canceled")
    .reduce((total, invoice) => total + invoice.totalAmount, 0);

  const totalAmountDue = Object.values(invoicesByCraftRecord)
    .flat()
    .filter((invoice) => invoice.status !== "canceled")
    .reduce((total, invoice) => total + invoice.amountDue, 0);

  const totalPaid = totalInvoiceAmount - totalAmountDue;

  const allTasks = Object.values(tasksByCraftRecord).flat();
  const completedTasks = allTasks.filter(
    (task) => task.taskStatus === TaskStatus.COMPLETE,
  ).length;
  const pendingTasks = allTasks.length - completedTasks;

  const totalEstimates = Object.values(estimatesByCraftRecord).flat().length;
  const totalPhotos = Object.values(photosByCraftRecord).flat().length;

  const handleViewPhotos = () => {
    setIsPhotoGalleryOpen(true);
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6 flex items-center justify-between">
        <ProjectHeader
          project={project}
          craftRecords={craftRecords}
          customer={customer}
        />
        <div className="flex gap-2">
          <BaseButtonSecondary
            onClick={onEditProject}
            className="inline-flex items-center"
          >
            <svg
              className="-ml-1 mr-2 h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
            Edit Project
          </BaseButtonSecondary>
          <BaseButtonSecondary
            onClick={onGeneratePDF}
            disabled={generatingPDF}
            className="inline-flex items-center"
          >
            {generatingPDF ? (
              <>
                <svg
                  className="-ml-1 mr-2 h-4 w-4 animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Generating PDF...
              </>
            ) : (
              <>
                <svg
                  className="-ml-1 mr-2 h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Generate PDF
              </>
            )}
          </BaseButtonSecondary>
        </div>
      </div>

      <ProjectStats
        totalJobs={craftRecords.length}
        totalTasks={allTasks.length}
        completedTasks={completedTasks}
        pendingTasks={pendingTasks}
        totalInvoices={Object.values(invoicesByCraftRecord).flat().length}
        totalEstimates={totalEstimates}
        totalPhotos={totalPhotos}
        totalAttachments={attachments.length}
        totalValue={totalInvoiceAmount}
        paidAmount={totalPaid}
        dueAmount={totalAmountDue}
        onViewPhotos={handleViewPhotos}
      />

      <div className="my-6">
        <h2 className="mb-4 text-xl font-semibold text-gray-800">
          Jobs in this Project
        </h2>
        <CraftRecordList
          craftRecords={craftRecords}
          tasksByCraftRecord={tasksByCraftRecord}
          invoicesByCraftRecord={invoicesByCraftRecord}
          estimatesByCraftRecord={estimatesByCraftRecord}
          photosByCraftRecord={photosByCraftRecord}
          userDisplayNamesMap={userDisplayNamesMap}
          isUserDisplayNamesLoading={isUserDisplayNamesLoading}
          siteKey={siteKey}
        />
      </div>

      {/* Photo Gallery Dialog */}
      <ProjectPhotoGallery
        isOpen={isPhotoGalleryOpen}
        onClose={() => setIsPhotoGalleryOpen(false)}
        photosByCraftRecord={photosByCraftRecord}
        craftRecords={craftRecords}
        projectID={project.id}
        siteKey={siteKey}
      />
    </div>
  );
}
